import { Injectable } from '@angular/core';
import {
  ApiCallHandler,
  EoHttpClient,
  IDataService,
  IHttpParams,
  PaginatedSearchResult,
  STATUS,
} from 'ngx-eo-web-components';
import { CoreService } from '../../modules/core/core.service';
import { ISortParams } from '../../modules/shared/models/sort.model';
import { map, Observable } from 'rxjs';
import { Surgery } from '../../modules/core/models/surgery.model';
import { SurgeryDoctor } from '../../modules/core/models/surgery-doctor.model';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SurgeryService implements IDataService<Surgery> {
  private searchParams: {
    hospitalID: string;
    clinicID: string | null;
    hospitalAvailabilityId: string | null;
    patientAmka: string | null;
    status: string | null;
    uniqueListPatientCode?: string | null;
    sortParams?: ISortParams | null;
  } | null = null;

  constructor(private http: EoHttpClient, private coreService: CoreService) {}
  public searchCh = new ApiCallHandler<PaginatedSearchResult<Surgery> | null>();
  public surgeryScheduleCallHandler = new ApiCallHandler<any>();
  public resurgeryScheduleCallHandler = new ApiCallHandler<any>();
  public surgeryCancelCallHandler = new ApiCallHandler<any>();
  public surgeryCompleteCallHandler = new ApiCallHandler<any>();
  public surgeryReturnCallHandler = new ApiCallHandler<any>();
  public surgeryScheduledCountCallHandler = new ApiCallHandler<number>();
  public surgeryUnScheduledCountCallHandler = new ApiCallHandler<number>();
  public surgeryFetchCallHandler = new ApiCallHandler<Surgery>();
  public surgeryFetchPrivateCallHandler = new ApiCallHandler<Surgery>();
  public surgeryReceiveCallHandler = new ApiCallHandler<any>();
  public surgeryDowmnloadPdfCallHandler = new ApiCallHandler<Blob>();

  init() {
    this.searchCh.init();
    this.surgeryScheduleCallHandler.init(null);
    this.resurgeryScheduleCallHandler.init(null);
    this.surgeryCancelCallHandler.init(null);
    this.surgeryCompleteCallHandler.init(null);
    this.surgeryReturnCallHandler.init(null);
    this.surgeryScheduledCountCallHandler.init(0);
    this.surgeryUnScheduledCountCallHandler.init(0);
    this.surgeryFetchCallHandler.init();
    this.surgeryFetchPrivateCallHandler.init();
    this.surgeryReceiveCallHandler.init(null);
  }

  getSurgeryById(surgeryId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Surgeries/fetch/${surgeryId}`,
      this.surgeryFetchCallHandler
    );
    return this.http.get<Surgery>(param);
  }

  getPrivateSurgeryByCode(code: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Surgeries/fetch/private/${code}`,
      this.surgeryFetchPrivateCallHandler
    );
    return this.http.get<Surgery>(param);
  }

  searchAssignedSurgeries(
    searchParams: {
      hospitalID: string;
      clinicID: string | null;
      hospitalAvailabilityId: string | null;
      patientAmka: string | null;
      status: string | null;
      uniqueListPatientCode?: string | null;
      sortParams?: ISortParams | null;
    },
    pagesize: number,
    page: number
  ): Observable<PaginatedSearchResult<Surgery>> {
    // Save searchParams for reuse
    this.searchParams = searchParams;

    const param: IHttpParams = this.coreService.initCall(
      `/v1/Surgeries/searchAssigned/${pagesize}/${page}`,
      this.searchCh,
      searchParams
    );

    return this.http.post<PaginatedSearchResult<Surgery>>(param);
  }

  getSavedSearchParams() {
    return this.searchParams;
  }

  scheduleSurgery(surgeryScheduleBind: {
    surgeryID: string;
    surgeryDateTime: Date;
  }) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/schedule',
      this.surgeryScheduleCallHandler,
      surgeryScheduleBind
    );
    return this.http.post(param);
  }

  reScheduleSurgery(surgeryScheduleBind: {
    surgeryID: string;
    surgeryDateTime: Date;
  }) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/reschedule',
      this.resurgeryScheduleCallHandler,
      surgeryScheduleBind
    );
    return this.http.post(param);
  }

  cancelSurgery(surgeryCancelBind: {
    surgeryID: string;
    surgeryCancelReasonOptionID: string;
    surgeryCancelComments?: string;
  }) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/cancel',
      this.surgeryCancelCallHandler,
      surgeryCancelBind
    );
    return this.http.post(param);
  }

  completeSurgery(surgeryCompleteBind: {
    surgeryID: string;
    entryTicketNumber: string;
    isGDPR: boolean;
    drgID: string;
    isDrgConsent: boolean;
    surgeryDoctors: SurgeryDoctor[];
  }) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/complete',
      this.surgeryCompleteCallHandler,
      surgeryCompleteBind
    );
    return this.http.post(param);
  }

  returnSurgery(surgeryID: string) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/return',
      this.surgeryReturnCallHandler,
      { SurgeryID: surgeryID }
    );
    return this.http.post(param);
  }

  receiveSurgery(surgeryReceiveBind: {
    hospitalID: string;
    clinicID: string;
    surgeryID: string;
    hospitalAvailabilityID: string;
    surgeryDateTime: Date;
  }) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/receive',
      this.surgeryReceiveCallHandler,
      surgeryReceiveBind
    );
    return this.http.post(param);
  }

  getScheduledCount(
    hospitalId?: string,
    clinicId?: string
  ): Observable<number> {
    let bindObj: {
      hospitalID?: string | null;
      clinicID?: string | null;
    } = {
      hospitalID: hospitalId ?? null,
      clinicID: clinicId ?? null,
    };

    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/scheduledCount',
      this.surgeryScheduledCountCallHandler,
      bindObj
    );
    return this.http.post<number>(param);
  }

  getUnScheduledCount(
    hospitalId?: string,
    clinicId?: string
  ): Observable<number> {
    let bindObj: {
      hospitalID?: string | null;
      clinicID?: string | null;
    } = {
      hospitalID: hospitalId ?? null,
      clinicID: clinicId ?? null,
    };

    const param: IHttpParams = this.coreService.initCall(
      '/v1/Surgeries/unscheduledCount',
      this.surgeryUnScheduledCountCallHandler,
      bindObj
    );
    return this.http.post<number>(param);
  }

  downloadPdf(surgeryId: string): Observable<{ filename: string, data: Blob }> {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Surgeries/downloadPdf`,
      this.surgeryDowmnloadPdfCallHandler,
      { SurgeryID: surgeryId }
    );
    return this.http.downloadFile(param);
  }

  downloadExecutionForm(surgeryId: string): Observable<{ filename: string, data: Blob }> {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Surgeries/downloadExecutionForm/${surgeryId}`,
      this.surgeryDowmnloadPdfCallHandler,
      null
    );
    return this.http.downloadFile(param);
  }

}
